import NumberInput from 'components/forms/inputs/rhf-inputs/NumberInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import InfoTooltip from 'components/InfoTooltip';
import { InvoiceAutoGenerationScheduleReadonlyField } from 'components/InvoiceAutoGenerationScheduleInfo';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { Company } from 'types/companies';
import { Contract } from 'types/contracts';
import {
    FinancialCondition,
    FinancialConditionCreateDto,
    FinancialConditionForm,
    FinancialConditionScopeType,
} from 'types/financial-conditions';
import { PricingLimitationType } from 'types/matters';
import {
    composeDateFormat,
    DATE_ORDERS,
    DATE_SEPARATORS,
    DateOrder,
    DateSeparator,
    decomposeDateFormat,
} from 'utils/dateFormat';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';

import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import PhoneNumberInput from 'components/forms/inputs/rhf-inputs/PhoneNumberInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { isValidEmail } from 'components/forms/inputs/rhf-validators/validators';

interface Props {
    titleId?: string;
    onChange: (value: FinancialConditionCreateDto) => void;
    onClose: () => void;
    financialCondition?: FinancialCondition;
    classifiers: Record<string, ClassifierData>;
    scopeType: FinancialConditionScopeType;
    company?: Company;
    contract?: Contract;
    includeCompanyFields?: boolean;
}

const ORDER_LABELS: Record<DateOrder, string> = {
    YMD: 'YYYY-MM-DD',
    DMY: 'DD-MM-YYYY',
    MDY: 'MM-DD-YYYY',
};

const dateOrderOptions: { label: string; value: DateOrder }[] = DATE_ORDERS.map((value) => ({
    label: ORDER_LABELS[value],
    value,
}));

const dateSeparatorOptions: { label: string; value: DateSeparator }[] = DATE_SEPARATORS.map((value) => ({
    label: value,
    value,
}));

const buildCompanyDefaults = (company: Company, classifiers: Record<string, ClassifierData>) => {
    const decomposed = decomposeDateFormat(company.customDateFormat);
    const countryOptions = getValidOptionsFromClassifier(classifiers['COUNTRY']) as {
        label: string;
        value: string;
    }[];
    return {
        ...company,
        isDefault: company.isDefault ?? false,
        isVatLiable: company.isVatLiable ?? false,
        isActive: company.isActive ?? true,
        email: company.email ?? '',
        phone: company.phone ?? '',
        vatRegCode: company.vatRegCode ?? '',
        countryCode: countryOptions.find((val) => val.value === company?.countryCode) ?? null,
        dateOrder: decomposed ? (dateOrderOptions.find((o) => o.value === decomposed.order) ?? null) : null,
        dateSeparator: decomposed ? (dateSeparatorOptions.find((o) => o.value === decomposed.separator) ?? null) : null,
    };
};

const FinancialConditionEditModal = (props: Props) => {
    const {
        titleId = 'financial-conditions.edit-financial-condition',
        financialCondition,
        classifiers,
        onClose,
        onChange,
        scopeType,
        company,
        contract,
        includeCompanyFields,
    } = props;

    const defaultValues: FinancialConditionForm = useMemo(
        () => ({
            scopeTypeCode: scopeType,
            companyId: scopeType === FinancialConditionScopeType.COMPANY && company?.id ? company.id : null,
            contractId: scopeType === FinancialConditionScopeType.CONTRACT && contract?.id ? contract.id : null,
            paymentDeadlineInDays: financialCondition?.paymentDeadlineInDays ?? 0,
            prepaymentPaymentDeadlineInDays: financialCondition?.prepaymentPaymentDeadlineInDays ?? null,
            invoicingIntervalType:
                (
                    getValidOptionsFromClassifier(classifiers['INVOICING_INTERVAL_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === financialCondition?.invoicingIntervalTypeCode) ?? null,
            casesDefaultFeeArrangementType:
                (
                    getValidOptionsFromClassifier(classifiers['PRICING_LIMITATION_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                )
                    // Exclude SUCCESS_FEE so a value stored out-of-band can't be hydrated/re-persisted (it is
                    // never selectable in the dropdown either) — keeps the field consistent with the case form.
                    .filter((option) => option.value !== PricingLimitationType.SUCCESS_FEE)
                    .find((val) => val.value === financialCondition?.casesDefaultFeeArrangementTypeCode) ?? null,
            interestRatePct: financialCondition?.interestRatePct ?? '0',
            interestRateB2cPct: financialCondition?.interestRateB2cPct ?? '0',
            currencyType:
                (
                    getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === financialCondition?.currencyTypeCode) ?? null,
            company: company ? buildCompanyDefaults(company, classifiers) : null,
        }),
        [scopeType, company, contract, financialCondition, classifiers]
    );

    const { handleSubmit, control, setValue, getValues } = useForm<FinancialConditionForm>({
        defaultValues: defaultValues,
        values: defaultValues,
    });

    const validEmail = useCallback((val: unknown) => isValidEmail(val, t('validation.email-must-be-valid')), []);
    const [isVatLiable] = useWatch({ control, name: ['company.isVatLiable'] });
    const [selectedInvoicingInterval] = useWatch({ control, name: ['invoicingIntervalType'] });

    const handleOnChange = (data: FinancialConditionForm) => {
        onChange(mapFormDataToDto(data, includeCompanyFields));
        onClose();
    };
    const onSave = async () => {
        await handleSubmit((data) => handleOnChange(data))();
    };

    const ScopeDescription = useMemo(() => {
        let contractDescription = '-';
        if (scopeType === FinancialConditionScopeType.COMPANY) {
            contractDescription = company?.name ?? '-';
        } else {
            const contractType = getClassifierText(classifiers?.['CONTRACT_TYPE'], contract?.contractTypeCode) ?? '-';
            contractDescription = `${contractType} nr. ${contract?.contractNumber ?? '-'}`;
        }

        return (
            <div>
                <span className="text-caption text-fg-secondary">
                    {scopeType === FinancialConditionScopeType.COMPANY ? t('company') : t('contract')}
                </span>
                <p className="text-body1">{contractDescription}</p>
            </div>
        );
    }, [classifiers, company, contract, scopeType]);

    const invoicingIntervalOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['INVOICING_INTERVAL_TYPE']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    // "Success fee" is hidden until further analysis, mirroring the case fee-arrangement dropdown.
    const feeArrangementOptions = useMemo(
        () =>
            (
                (getValidOptionsFromClassifier(classifiers['PRICING_LIMITATION_TYPE']) as {
                    label: string;
                    value: string;
                }[]) ?? []
            ).filter((option) => option.value !== PricingLimitationType.SUCCESS_FEE),
        [classifiers]
    );

    const currencyOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    const countryOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['COUNTRY']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    return (
        <>
            <ModalHeader title={t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-[23px] pt-[15px] pb-[45px]">
                <div className="flex flex-col gap-4">
                    {ScopeDescription}
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <NumberInput
                                name="paymentDeadlineInDays"
                                control={control}
                                label="financial-conditions.payment-deadline"
                                placeholder="forms.enter-payment-deadline"
                                required
                                inputProps={{ min: 0, max: 120 }}
                                rightLabelComponent={
                                    <InfoTooltip
                                        text={[
                                            'financial-conditions.tooltips.payment-deadline',
                                            'financial-conditions.tooltips.eu-late-payment-directive',
                                        ]}
                                    />
                                }
                            />
                        </div>
                        <div className="flex-1">
                            <SelectInput
                                setValue={setValue}
                                control={control}
                                label="financial-conditions.invoicing-interval"
                                required
                                name="invoicingIntervalType"
                                options={invoicingIntervalOptions}
                                getOptionLabel="label"
                                getOptionIdentifier="value"
                                placeholder="forms.select-invoicing-interval"
                                rightLabelComponent={
                                    <InfoTooltip text={'financial-conditions.tooltips.invoicing-interval'} />
                                }
                            />
                        </div>
                    </div>
                    <InvoiceAutoGenerationScheduleReadonlyField
                        invoicingIntervalCode={selectedInvoicingInterval?.value}
                    />
                    <div className="w-full">
                        <NumberInput
                            name="prepaymentPaymentDeadlineInDays"
                            control={control}
                            label="financial-conditions.prepayment-payment-deadline"
                            placeholder="forms.enter-payment-deadline"
                            inputProps={{ min: 0, max: 120 }}
                        />
                    </div>
                    {scopeType === FinancialConditionScopeType.COMPANY && (
                        <div className="w-full">
                            <SelectInput
                                setValue={setValue}
                                control={control}
                                label="financial-conditions.cases-default-fee-arrangement"
                                name="casesDefaultFeeArrangementType"
                                options={feeArrangementOptions}
                                getOptionLabel="label"
                                getOptionIdentifier="value"
                                placeholder="forms.select-fee-arrangement"
                                rightLabelComponent={
                                    <InfoTooltip text={'financial-conditions.tooltips.cases-default-fee-arrangement'} />
                                }
                            />
                        </div>
                    )}
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <PercentageInput
                                name="interestRatePct"
                                control={control}
                                label="financial-conditions.interest-rate"
                                placeholder="forms.enter-interest-rate"
                                required
                                inputProps={{ max: 99 }}
                                rightLabelComponent={
                                    <InfoTooltip text={'financial-conditions.tooltips.interest-rate'} />
                                }
                            />
                        </div>
                        <div className="flex-1">
                            <PercentageInput
                                name="interestRateB2cPct"
                                control={control}
                                label="financial-conditions.interest-rate-b2c"
                                placeholder="forms.enter-interest-rate-b2c"
                                required
                                inputProps={{ max: 99 }}
                                rightLabelComponent={
                                    <InfoTooltip text={'financial-conditions.tooltips.interest-rate'} />
                                }
                            />
                        </div>
                        <div className="flex-1">
                            <SelectInput
                                setValue={setValue}
                                control={control}
                                label="currency"
                                required
                                name="currencyType"
                                options={currencyOptions}
                                getOptionLabel="label"
                                getOptionIdentifier="value"
                                placeholder="forms.select-currency"
                                rightLabelComponent={<InfoTooltip text={'financial-conditions.tooltips.currency'} />}
                            />
                        </div>
                    </div>
                    {includeCompanyFields && (
                        <>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <TextInput
                                        control={control}
                                        label="name"
                                        placeholder="forms.enter-name"
                                        name="company.name"
                                        required
                                    />
                                </div>
                                <div className="flex-1">
                                    <TextInput
                                        control={control}
                                        label="financial-conditions.company.reg-code"
                                        placeholder="forms.enter-reg-code"
                                        name="company.regCode"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <CheckboxInput control={control} name="company.isVatLiable" label="is-vat-liable" />
                                </div>
                                <div className="flex-1">
                                    <TextInput
                                        control={control}
                                        label="financial-conditions.company.vat-number"
                                        placeholder="forms.enter-vat-number"
                                        name="company.vatRegCode"
                                        required={isVatLiable ?? undefined}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <TextInput
                                        control={control}
                                        label="address"
                                        placeholder="forms.enter-address"
                                        name="company.address"
                                        required
                                    />
                                </div>
                                <div className="flex-1">
                                    <TextInput
                                        control={control}
                                        label="city"
                                        placeholder="forms.enter-city"
                                        name="company.cityName"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <SelectInput
                                        setValue={setValue}
                                        control={control}
                                        label="address-form.country"
                                        name="company.countryCode"
                                        options={countryOptions}
                                        getOptionLabel="label"
                                        getOptionIdentifier="value"
                                        placeholder="address-form.country"
                                        required
                                    />
                                </div>
                                <div className="flex-1">
                                    <TextInput
                                        control={control}
                                        label="email"
                                        placeholder="forms.enter-email"
                                        name="company.email"
                                        validate={validEmail}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <PhoneNumberInput
                                        name="company.phone"
                                        control={control}
                                        label="phone"
                                        placeholder="forms.enter-phone-number"
                                    />
                                </div>
                                <div className="flex-1">
                                    <CheckboxInput
                                        control={control}
                                        name="company.isDefault"
                                        label="financial-conditions.company.is-default"
                                        readonly={getValues('company.isDefault') ?? false}
                                    />
                                    <CheckboxInput
                                        control={control}
                                        name="company.isActive"
                                        label="financial-conditions.company.is-active"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <SelectInput
                                        setValue={setValue}
                                        control={control}
                                        label="financial-conditions.company.date-order"
                                        name="company.dateOrder"
                                        options={dateOrderOptions}
                                        getOptionLabel="label"
                                        getOptionIdentifier="value"
                                        placeholder="forms.select-date-order"
                                        validate={(value, formValues) =>
                                            !!value === !!formValues?.company?.dateSeparator ||
                                            t('financial-conditions.company.date-format-both-or-neither')
                                        }
                                    />
                                </div>
                                <div className="flex-1">
                                    <SelectInput
                                        setValue={setValue}
                                        control={control}
                                        label="financial-conditions.company.date-separator"
                                        name="company.dateSeparator"
                                        options={dateSeparatorOptions}
                                        getOptionLabel="label"
                                        getOptionIdentifier="value"
                                        placeholder="forms.select-date-separator"
                                        validate={(value, formValues) =>
                                            !!value === !!formValues?.company?.dateOrder ||
                                            t('financial-conditions.company.date-format-both-or-neither')
                                        }
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    void onSave();
                }}
            />
        </>
    );
};

export default FinancialConditionEditModal;

const mapFormDataToDto = (
    form: FinancialConditionForm,
    includeCompanyFields: boolean | undefined
): FinancialConditionCreateDto => ({
    scopeTypeCode: form.scopeTypeCode,
    companyId: form?.companyId ?? null,
    contractId: form?.contractId ?? null,
    paymentDeadlineInDays: form?.paymentDeadlineInDays ?? 0,
    prepaymentPaymentDeadlineInDays: form?.prepaymentPaymentDeadlineInDays ?? null,
    invoicingIntervalTypeCode: form?.invoicingIntervalType?.value ?? null,
    casesDefaultFeeArrangementTypeCode: form?.casesDefaultFeeArrangementType?.value ?? null,
    interestRatePct: form?.interestRatePct ?? '0',
    interestRateB2cPct: form?.interestRateB2cPct ?? '0',
    currencyTypeCode: form?.currencyType?.value ?? null,
    company: includeCompanyFields
        ? {
              name: form?.company?.name || undefined,
              regCode: form?.company?.regCode || undefined,
              // `?? undefined` (not `|| undefined`): `false` must reach the backend — the columns are NOT NULL
              isVatLiable: form?.company?.isVatLiable ?? undefined,
              vatRegCode: form?.company?.vatRegCode || undefined,
              customDateFormat:
                  form?.company?.dateOrder?.value && form?.company?.dateSeparator?.value
                      ? composeDateFormat(
                            form.company.dateOrder.value as DateOrder,
                            form.company.dateSeparator.value as DateSeparator
                        )
                      : null,
              address: form?.company?.address || undefined,
              cityName: form?.company?.cityName || undefined,
              countryCode: form?.company?.countryCode?.value || undefined,
              email: form?.company?.email || undefined,
              phone: form?.company?.phone || undefined,
              isDefault: form?.company?.isDefault ?? undefined,
              isActive: form?.company?.isActive,
          }
        : undefined,
});
